import FJMathUtil from '../../util/FJMathUtil';
import FJCoreConfig from '../FJCoreConfig';
import FJTrack, { TrackNode } from './FJTrack';
import $ from 'jquery';

export const LineTrackNode = {
    START_POINT: 10,
    END_POINT: 11,
    CONTROL_POINT: 12,
};

const ValidNodeTypes = [
    LineTrackNode.START_POINT,
    LineTrackNode.END_POINT,
    LineTrackNode.CONTROL_POINT,
    TrackNode.ROTATE,
];

class FJLineTrack extends FJTrack {
    constructor(props) {
        super(props);
        this._showControlPoint = props?.showControlPoint || false;
    }

    // 判断 node 是否无效
    _isInvalidNode(node) {
        if (!ValidNodeTypes.includes(node.type)) {
            return true;
        }

        // 如果没有启用旋转节点，则隐藏旋转节点
        if (node.type === TrackNode.ROTATE && !this._hasRotationNode) {
            return true;
        }

        if (node.type === LineTrackNode.CONTROL_POINT && !this._showControlPoint) {
            return true;
        }

        return false;
    }

    _initNodesView() {
        const validNodeTypes = [LineTrackNode.START_POINT, LineTrackNode.END_POINT, LineTrackNode.CONTROL_POINT];

        validNodeTypes.forEach(nodeType => {
            let node = document.createElement('div');

            node.style.position = 'absolute';
            node.style.width = 2 * this._trackNodeRadius + 'px';
            node.style.height = 2 * this._trackNodeRadius + 'px';
            node.style.visibility = 'hidden';
            node.style.border = '2px solid #FFFFFF';
            node.style.borderRadius = this._trackNodeRadius + 'px';
            node.style.boxShadow = this._trackNodeBoxShadow;

            this._nodeView.push(node);
            this._trackLayer.appendChild(node);
        });

        // 创建旋转节点（保留原有功能）
        let rotateNode = document.createElement('div');

        // 设置旋转节点样式
        rotateNode.style.position = 'absolute';
        rotateNode.style.width = 2 * this._rotateNodeRadius + 'px';
        rotateNode.style.height = 2 * this._rotateNodeRadius + 'px';
        rotateNode.style.borderRadius = this._rotateNodeRadius + 'px';
        rotateNode.style.background = '#ffffff';
        rotateNode.style.boxShadow = this._trackNodeBoxShadow;
        rotateNode.style.visibility = 'hidden';
        rotateNode.style.left = '50%';
        rotateNode.style.top = `calc(100% + ${this._rotateLength}px)`;
        rotateNode.style.transform = 'translateX(-50%)';

        this._nodeView.push(rotateNode);
        this._trackLayer.appendChild(rotateNode);
    }

    _init() {
        if (this._trackLayer) {
            return;
        }
        this._nodes = [];
        this._nodeView = [];
        this._initTrackLayer();
        this._initWireframe();
        this._initNodesView();
        this._initRotateSVG();
    }

    _trackNodes() {
        let rect = this._getTrackRect();
        if (!rect) return [];

        let startPoint, endPoint, controlPoint;

        if (this._owner && this._owner._startPoint && this._owner._endPoint) {
            const startRatio = this._owner._startPoint;
            const endRatio = this._owner._endPoint;

            // 转换为实际像素坐标
            startPoint = {
                x: rect.x + startRatio.x * rect.width,
                y: rect.y + startRatio.y * rect.height,
            };

            endPoint = {
                x: rect.x + endRatio.x * rect.width,
                y: rect.y + endRatio.y * rect.height,
            };
        } else {
            // 如果没有找到线条元素的坐标，使用默认值
            startPoint = { x: rect.x, y: rect.y + rect.height / 2 };
            endPoint = { x: rect.x + rect.width, y: rect.y + rect.height / 2 };
        }

        if (this._showControlPoint) {
            controlPoint = {
                x: rect.x + this._owner._lineTypeData.controlPoint.x * rect.width,
                y: rect.y + this._owner._lineTypeData.controlPoint.y * rect.height,
            };
        } else {
            // 不会显示控制点
            controlPoint = {
                x: 0,
                y: 0,
            };
        }

        // 转换为相对于轨道容器的坐标
        const relativeStartPoint = {
            x: startPoint.x - rect.x,
            y: startPoint.y - rect.y,
        };

        const relativeEndPoint = {
            x: endPoint.x - rect.x,
            y: endPoint.y - rect.y,
        };

        const relativeControlPoint = {
            x: controlPoint.x - rect.x,
            y: controlPoint.y - rect.y,
        };

        let nodes = [
            { position: relativeStartPoint, type: LineTrackNode.START_POINT },
            { position: relativeEndPoint, type: LineTrackNode.END_POINT },
            { position: relativeControlPoint, type: LineTrackNode.CONTROL_POINT },
        ];

        // 添加旋转节点（如果启用）
        if (this._hasRotationNode) {
            let rotateNode = {
                x: rect.width / 2,
                y: rect.height + (this._rotateNodeRadius + this._rotateLength),
            };
            // 旋转点自动适配
            let autoRotateNode = { ...rotateNode };
            if (this._hasRotateAutoAdapt) {
                autoRotateNode = this._getRotatePositionAndUpdateRotateDom(autoRotateNode);
            }
            rotateNode = autoRotateNode ? autoRotateNode : rotateNode;
            nodes.push({ position: rotateNode, type: TrackNode.ROTATE });
        }

        return nodes;
    }

    // 重写 _drawNodes 方法，确保节点正确定位
    _drawNodes() {
        if (!this._hasNode || !this._trackLayer) return;

        for (let i = 0; i < this._nodes.length; i++) {
            let node = this._nodes[i];
            let nodeView = this._nodeView[i];

            if (this._isInvalidNode(node)) {
                nodeView.style.visibility = 'hidden';
                continue;
            }

            nodeView.style.visibility = 'visible';

            // 根据节点类型设置位置
            if (node.type === LineTrackNode.START_POINT || node.type === LineTrackNode.END_POINT) {
                // 线条端点节点的位置设置
                nodeView.style.left = node.position.x - this._trackNodeRadius + 'px';
                nodeView.style.top = node.position.y - this._trackNodeRadius + 'px';
            }

            // 曲线控制点的位置设置
            if (node.type === LineTrackNode.CONTROL_POINT) {
                nodeView.style.left = node.position.x - this._trackNodeRadius + 'px';
                nodeView.style.top = node.position.y - this._trackNodeRadius + 'px';
            }

            // 旋转节点的位置由父类的逻辑处理，不需要在这里设置
            // 设置选中状态的颜色
            if (this._selectedNode === node.type) {
                if (node.type === TrackNode.ROTATE) {
                    this._rotateSVG.querySelector('path').style.fill = FJCoreConfig.trackRotateNormalColor;
                } else {
                    this._updateSelectedTrackNodeColor(nodeView);
                }
            } else {
                if (node.type === TrackNode.ROTATE) {
                    this._rotateSVG.querySelector('path').style.fill = FJCoreConfig.trackRotateNormalColor;
                } else {
                    this._updateNormalTrackNodeColor(nodeView);
                }
            }
        }
    }

    trackNodeAtPoint(point) {
        let result = -1;
        if (!point || !point.hasOwnProperty('x') || !point.hasOwnProperty('y')) {
            return result;
        }
        let p = this.convertMousePointToTrack(point);
        for (let i = 0; i < this._nodes.length; i++) {
            let node = this._nodes[i];
            if (this._isInvalidNode(node)) continue;

            if (this._hitTestAtPoint(node.position, p)) {
                result = node.type;
                if (this._selectedNode !== i) {
                    this._selectedNode = i;
                    if (this._selected) this._drawNodes();
                }
                break;
            }
        }

        if (result !== -1) {
            // TODO: 这里的返回如果是父类有的元素则保持和父类一致
            return result;
        }

        if (this._selectedNode !== -1) {
            this._selectedNode = -1;
            if (this._selected) this._drawNodes();
        }

        // 如果该点不在画布区域内，防止某些超出画布区域的画中画可以被替换
        if (this._viewRect && !FJMathUtil.isPointInRect(point, this._viewRect)) {
            return -1;
        }

        let rect = this.renderRect();
        if (rect) {
            const height = Math.max(rect.height, 20);

            const offset = (height - rect.height) / 2;

            rect.x = 0;
            rect.y = 0 - offset;
            rect.height = height;

            if (FJMathUtil.isPointInRect(p, rect)) {
                return TrackNode.MOVE;
            }
        }

        return -1;
    }

    setHasStartPoint(hasStartPoint) {
        this._hasStartPoint = hasStartPoint;

        if (!this._nodeView || this._nodeView.length === 0) this._init();
        if (!this._nodeView || this._nodeView.length === 0) this._init();
        const index = this._nodes.findIndex(node => node.type === LineTrackNode.START_POINT);
        this._nodeView[index].style.display = this._hasStartPoint ? 'block' : 'none';
    }

    setHasEndPoint(hasEndPoint) {
        this._hasEndPoint = hasEndPoint;
        if (!this._nodeView || this._nodeView.length === 0) this._init();
        const index = this._nodes.findIndex(node => node.type === LineTrackNode.END_POINT);
        this._nodeView[index].style.display = this._hasEndPoint ? 'block' : 'none';
    }

    /**
     * @desc  交互过程中临时控制是否显示
     * @param {*} hasControlPoint
     */
    setHasControlPoint(hasControlPoint) {
        this._hasControlPoint = hasControlPoint;
        if (!this._nodeView || this._nodeView.length === 0) this._init();
        const index = this._nodes.findIndex(node => node.type === LineTrackNode.CONTROL_POINT);
        this._nodeView[index].style.display = this._hasControlPoint ? 'block' : 'none';
    }

    /**
     * @desc  控制是否显示
     * @param {*} showControlPoint
     */
    setShowControlPoint(showControlPoint) {
        this._showControlPoint = showControlPoint;
    }

    _drawLine(isDottedLine, lineStyle) {
        let rect = this._getTrackRect();
        if (!rect) return;
        rect = FJMathUtil.integratedRect(rect);

        $(this._lineView).attr({ width: rect.width, height: rect.height });
        let lineRect = $(this._lineView).find('.lineRect');
        let lineRectBack = $(this._lineView).find('.lineRectBack');
        $(lineRect).attr(lineStyle);
        $(lineRectBack).attr(lineStyle);

        // 非曲线状态下不显示选中框
        if (lineRectBack) $(lineRectBack).css({ opacity: isDottedLine || !this._showControlPoint ? 0 : 1 });
        if (!this._showControlPoint) {
            $(lineRect).css({ opacity: 0 });
        }
    }
}

export default FJLineTrack;
